import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw, Send, AlertTriangle, CheckCircle, Clock, Mail, Eye, MousePointerClick, MailX } from 'lucide-react';
import { Campaign } from '@/services/campaignService';
import { fetchCampaignQueueItems } from '@/services/campaignQueueService';
import { format } from 'date-fns';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}

const queueStatusConfig: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'; label: string; icon: React.ElementType }> = {
  delivered: { variant: 'success', label: 'Delivered', icon: CheckCircle },
  sent: { variant: 'success', label: 'Sent', icon: CheckCircle },
  opened: { variant: 'default', label: 'Opened', icon: Eye },
  clicked: { variant: 'default', label: 'Clicked', icon: MousePointerClick },
  bounced: { variant: 'destructive', label: 'Bounced', icon: MailX },
  dropped: { variant: 'destructive', label: 'Dropped', icon: AlertTriangle },
  failed: { variant: 'destructive', label: 'Failed', icon: AlertTriangle },
  pending: { variant: 'warning', label: 'Pending', icon: Clock },
  processing: { variant: 'secondary', label: 'Processing', icon: RefreshCw },
  deferred: { variant: 'warning', label: 'Deferred', icon: Clock },
  spam_report: { variant: 'destructive', label: 'Spam', icon: MailX },
};

const CampaignDetailDialog: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const { data: queueItems = [], isLoading, refetch } = useQuery({
    queryKey: ['campaign-queue', campaign.id],
    queryFn: () => fetchCampaignQueueItems(campaign.id),
    enabled: open,
    refetchInterval: campaign.status === 'sending' ? 5000 : false,
  });

  const stats = [
    { label: 'Total Recipients', value: campaign.total_recipients, icon: Mail, color: 'text-foreground' },
    { label: 'Sent', value: campaign.sent_count, icon: Send, color: 'text-green-600' },
    { label: 'Delivered', value: campaign.delivered_count, icon: CheckCircle, color: 'text-emerald-600' },
    { label: 'Failed', value: campaign.failed_count, icon: AlertTriangle, color: 'text-destructive' },
    { label: 'Opened', value: campaign.opened_count, icon: Eye, color: 'text-blue-600' },
    { label: 'Bounced', value: campaign.bounced_count, icon: MailX, color: 'text-orange-600' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Campaign Details: {campaign.name}
          </DialogTitle>
        </DialogHeader>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {stats.map(s => {
            const Icon = s.icon;
            return (
              <Card key={s.label}>
                <CardContent className="p-3 text-center">
                  <Icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
                  <div className="text-lg font-bold">{s.value}</div>
                  <div className="text-[10px] text-muted-foreground">{s.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recipient Log */}
        <div className="flex items-center justify-between mt-2">
          <h3 className="text-sm font-medium">Recipient Log</h3>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
          </Button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden sm:table-cell">Time</TableHead>
                <TableHead className="hidden md:table-cell">Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : queueItems.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No recipients found</TableCell></TableRow>
              ) : queueItems.map(item => {
                const displayStatus = item.delivery_status || item.status;
                const cfg = queueStatusConfig[displayStatus] || queueStatusConfig.pending;
                const StatusIcon = cfg.icon;
                const timestamp = item.sent_at || item.failed_at || item.created_at;
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="text-sm">{item.recipient_email}</div>
                      {item.recipient_name && <div className="text-xs text-muted-foreground">{item.recipient_name}</div>}
                    </TableCell>
                    <TableCell className="text-sm max-w-[150px] truncate">{item.subject}</TableCell>
                    <TableCell>
                      <Badge variant={cfg.variant} className="gap-1">
                        <StatusIcon className="h-3 w-3" />
                        {cfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">
                      {timestamp ? format(new Date(timestamp), 'MMM d, HH:mm') : '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-destructive max-w-[200px] truncate">
                      {item.error_message || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignDetailDialog;
